import type { Unit } from '../models/Corpus';

export async function parseHtmlCorpus(html: string): Promise<Unit[]> {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Clean up Gutenberg cruft if present
    // Markers: "*** START OF THE PROJECT GUTENBERG EBOOK" / "*** END"
    // Usually these are in <pre> tags or just raw text, but in HTML they might be inside specific elements.
    // For HTML, we usually look for body content.

    // Simple extraction strategy:
    // 1. Get body
    // 2. Remove known junk (script, style)
    // 3. Find units by <h1>-<h3> or anchors

    const body = doc.body;
    if (!body) return [];

    const units: Unit[] = [];
    let currentUnit: Partial<Unit> | null = null;
    let currentBuffer: string[] = [];
    let unitCount = 0;

    // Helper to commit current unit
    const commitUnit = () => {
        if (currentUnit && currentUnit.title) {
            const cleanText = currentBuffer.join('\n\n').trim();
            if (cleanText.length > 0) {
                // Generate content map (paragraphs)
                const paragraphs = cleanText.split(/\n\s*\n/).filter(p => p.trim().length > 0);
                const contentMap: Record<string, string> = {};
                paragraphs.forEach((p, idx) => {
                    contentMap[(idx + 1).toString()] = p.trim();
                });

                units.push({
                    id: currentUnit.id || `unit_${unitCount}`,
                    title: currentUnit.title,
                    order: unitCount,
                    text: cleanText,
                    loc: currentUnit.title, // Simple location
                    content: contentMap
                });
                unitCount++;
            }
        }
        currentBuffer = [];
        currentUnit = null;
    };

    // Strategy: Flatten DOM to text blocks and headings?
    // Or iterate over children of body?
    // Gutenberg HTML is usually flat: <h2>Chapter 1</h2> <p>...</p> <p>...</p>

    const children = Array.from(body.children);

    // Fallback: If no headings, treat whole as one unit?
    // Let's iterate

    children.forEach((el) => {
        const tag = el.tagName.toLowerCase();

        // Header detection
        if (['h1', 'h2', 'h3'].includes(tag)) {
            // New Unit
            commitUnit();

            const title = el.textContent?.trim() || `Chapter ${unitCount + 1}`;
            // Try to find an anchor for stable ID
            // Gutenberg often puts <a name="chap01"></a> right before or inside
            const anchor = el.querySelector('a[name]') || el.previousElementSibling?.querySelector('a[name]') || (el.previousElementSibling?.tagName === 'A' ? el.previousElementSibling : null);
            const id = anchor && (anchor as HTMLAnchorElement).name ? (anchor as HTMLAnchorElement).name : `ch_${unitCount + 1}`;

            currentUnit = {
                id,
                title
            };
        }
        else if (tag === 'hr') {
            // Sometimes HR denotes a break, but usually headings are better.
            // Ignore for now unless we are desparate.
        }
        else if (['p', 'div', 'blockquote'].includes(tag)) {
            if (!currentUnit) {
                // Determine if this is preamble or we should start a default unit
                // Check if it looks like Gutenberg header
                const text = el.textContent || '';
                if (text.includes('START OF THE PROJECT GUTENBERG EBOOK')) {
                    // Start after this
                    return;
                }
                if (text.includes('END OF THE PROJECT GUTENBERG EBOOK')) {
                    // Stop here
                    // Actually we can just stop processing?
                    return; // todo: implement stop
                }

                // If we haven't started a unit yet, maybe start a "Preamble" unit?
                if (units.length === 0) {
                    currentUnit = { id: 'preamble', title: 'Front Matter' };
                }
            }

            if (currentUnit) {
                const t = el.textContent?.trim();
                if (t) currentBuffer.push(t);
            }
        }
    });

    commitUnit(); // Commit last

    // Fallback: if no units found (no headings), chunk by length?
    if (units.length === 0) {
        // Just grab all text
        const allText = body.textContent || '';
        if (allText.trim().length > 0) {
            // Create ONE single unit for the whole text
            const contentMap: Record<string, string> = {};
            const paragraphs = allText.split(/\n\s*\n/).filter(p => p.trim().length > 0);
            paragraphs.forEach((p, idx) => {
                contentMap[(idx + 1).toString()] = p.trim();
            });

            units.push({
                id: `chapter_1`,
                title: `Chapter 1`,
                order: 0,
                text: allText,
                loc: `Chapter 1`,
                content: contentMap
            });
        }
    }

    return units;
}
